/**
 * Spectrum Badges — Client-side badge renderer
 *
 * Reads badge config JSON from <script> blocks output by spectrum-badges.liquid,
 * filters/sorts/groups badges, and injects them into product card slot containers.
 */
(function () {
  'use strict';

  const CARD_MAX_BADGES = 3;
  const MOBILE_MAX_BADGES = 2;
  const MOBILE_BREAKPOINT = 768;

  const IMAGE_SLOTS = ['corner_tl', 'corner_tr', 'corner_bl', 'corner_br', 'top_edge', 'bottom_edge'];
  const INFO_SLOTS = ['pre_title', 'price_adjacent', 'below_price'];

  /**
   * Parse suppression string into a set of suppressed badge types.
   * "all" suppresses everything. "sale,new_arrival" suppresses those types.
   */
  function parseSuppression(raw) {
    if (!raw || raw.trim() === '') return { all: false, types: new Set() };
    const trimmed = raw.trim().toLowerCase();
    if (trimmed === 'all') return { all: true, types: new Set() };
    return { all: false, types: new Set(trimmed.split(',').map(s => s.trim())) };
  }

  /**
   * Filter, sort, and group badge configs.
   */
  function processBadges(configs, suppression) {
    if (suppression.all) return {};

    const maxBadges = window.innerWidth < MOBILE_BREAKPOINT ? MOBILE_MAX_BADGES : CARD_MAX_BADGES;

    const active = configs
      .filter(b => {
        if (!b || b.status !== 'active') return false;
        if (suppression.types.has(b.badge_type)) return false;
        // Visibility date checks
        if (b.visibility_start && new Date(b.visibility_start) > new Date()) return false;
        if (b.visibility_end && new Date(b.visibility_end) < new Date()) return false;
        return true;
      })
      .sort((a, b) => (a.priority ?? 100) - (b.priority ?? 100))
      .slice(0, maxBadges);

    // Group by slot
    const grouped = {};
    for (const badge of active) {
      const slot = badge.slot || 'corner_tl';
      if (!grouped[slot]) grouped[slot] = [];
      grouped[slot].push(badge);
    }

    return grouped;
  }

  /**
   * Create a badge DOM element from config.
   */
  function createBadgeElement(config) {
    const tag = config.link_url ? 'a' : 'span';
    const el = document.createElement(tag);
    el.className = 'sb-badge';

    if (config.background_color) {
      el.style.backgroundColor = config.background_color;
    }
    if (config.text_color) {
      el.style.color = config.text_color;
    }
    if (config.link_url) {
      el.href = config.link_url;
    }

    // Icon (if present)
    if (config.icon_url) {
      const img = document.createElement('img');
      img.className = 'sb-badge__icon';
      img.src = config.icon_url;
      img.alt = config.icon_tooltip || '';
      img.loading = 'lazy';
      el.appendChild(img);
    }

    // Text
    if (config.text) {
      const textNode = document.createTextNode(config.text);
      el.appendChild(textNode);
    }

    // Tooltip
    if (config.icon_tooltip) {
      el.title = config.icon_tooltip;
    }

    // Accessibility
    if (config.text) {
      el.setAttribute('aria-label', config.text);
    } else if (config.icon_tooltip) {
      el.setAttribute('aria-label', config.icon_tooltip);
    }

    return el;
  }

  /**
   * Create or find a slot container element within the product card.
   */
  function getSlotContainer(card, slotName) {
    // Check if already created. When the card is a `<product-card>` that
    // has a nested `<quick-shop>` (which server-renders its own
    // `.sb-slot--corner_tl[data-sb-source="server"]` for the mini-PDP),
    // we must NOT reuse that slot — it belongs to the quick-shop modal,
    // not the card's image. Otherwise the JS-rendered badge stacks on
    // top of the server-rendered one (visible as 2 duplicate badges in
    // the mini-PDP and on Continue Buying / recently-viewed cards).
    let existing = card.querySelector('.sb-slot--' + slotName);
    if (existing) {
      const owningQuickShop = existing.closest('quick-shop');
      if (owningQuickShop && owningQuickShop !== card) {
        existing = null;
      }
    }
    if (existing) return existing;

    const container = document.createElement('div');
    container.className = 'sb-slot sb-slot--' + slotName;

    if (IMAGE_SLOTS.includes(slotName)) {
      // PDP: anchor to the static .product-single__media wrapper, not the
      // slick-translated slide content (otherwise the badge follows slide 0
      // off-screen on multi-variant carousels).
      const isPdp = card.matches && (card.matches('product-single') || card.matches('.product-single__media'));
      if (isPdp) {
        const mediaWrap = card.matches('.product-single__media') ? card : card.querySelector('.product-single__media');
        if (mediaWrap) {
          if (getComputedStyle(mediaWrap).position === 'static') {
            mediaWrap.style.position = 'relative';
          }
          mediaWrap.appendChild(container);
        }
      } else {
        // Card / quick-shop: insert into the image ratio container.
        const ratioContent = card.querySelector('.o-ratio__content') || card.querySelector('.product-card-top');
        if (ratioContent) {
          ratioContent.appendChild(container);
        }
      }
    } else if (slotName === 'below_price') {
      // Insert after the price element
      const priceEl = card.querySelector('product-card-price') || card.querySelector('.price');
      if (priceEl) {
        priceEl.parentNode.insertBefore(container, priceEl.nextSibling);
      }
    } else if (slotName === 'pre_title') {
      // Insert before the title
      const title = card.querySelector('.product-card__title');
      if (title && title.parentNode) {
        title.parentNode.insertBefore(container, title);
      }
    } else if (slotName === 'price_adjacent') {
      // Insert after price number
      const priceNum = card.querySelector('.price__number');
      if (priceNum && priceNum.parentNode) {
        priceNum.parentNode.insertBefore(container, priceNum.nextSibling);
      }
    }

    return container;
  }

  /**
   * Render badges for a single product card.
   */
  function renderCardBadges(dataScript) {
    // Card / quick-shop / PDP — match whichever wrapper this badge data lives inside.
    const card = dataScript.closest('product-card, quick-shop, product-single, .product-single__media');
    if (!card) return;

    // Avoid double-rendering
    if (card.hasAttribute('data-sb-rendered')) return;
    card.setAttribute('data-sb-rendered', '');

    let configs;
    try {
      configs = JSON.parse(dataScript.textContent);
    } catch (e) {
      console.warn('[spectrum-badges] Failed to parse badge data:', e);
      return;
    }

    if (!Array.isArray(configs) || configs.length === 0) return;

    const suppressRaw = dataScript.getAttribute('data-sb-suppress') || '';
    const suppression = parseSuppression(suppressRaw);
    const grouped = processBadges(configs, suppression);

    for (const [slotName, badges] of Object.entries(grouped)) {
      const slotEl = getSlotContainer(card, slotName);
      if (!slotEl) continue;

      for (const badge of badges) {
        slotEl.appendChild(createBadgeElement(badge));
      }
    }
  }

  /**
   * Initialize: find all badge data scripts and render.
   */
  function init() {
    const scripts = document.querySelectorAll('script[id^="sb-data-"]');
    scripts.forEach(renderCardBadges);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Observe for dynamically added product cards (infinite scroll, AJAX pagination)
  const observer = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const scripts = node.querySelectorAll ? node.querySelectorAll('script[id^="sb-data-"]') : [];
        scripts.forEach(renderCardBadges);
        // Also check if the node itself is a script
        if (node.matches && node.matches('script[id^="sb-data-"]')) {
          renderCardBadges(node);
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();
